// LLM generated - Model Claude 4 Sonnet
import { SimpleGrid, Stack, Skeleton } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { Title } from '~/domains/design-system/Title';
import { PaymentType } from '~/common/enums/payments.enum';
import { reportEvent } from '~/domains/analytics';
import { useAeropayLinkedBankAccounts } from '~/domains/payments/hooks/useAeropayLinkedBankAccounts';

import { useWithdrawalFormContext } from '../context/withdrawal.context';
import { useWithdrawalPaymentMethods } from '../hooks/useWithdrawalPaymentMethods';

import { PaymentTypeRadio } from './PaymentTypeRadio';

type WithdrawalPaymentMethodsProps = {
  isAeropayEnabled: boolean;
  isLoadingPaymentMethods?: boolean;
  onWithdrawalMethodSelect?: () => void;
};

export function WithdrawalPaymentMethods({
  isAeropayEnabled,
  isLoadingPaymentMethods = false,
  onWithdrawalMethodSelect,
}: WithdrawalPaymentMethodsProps) {
  const { t } = useTranslation('payments');
  const form = useWithdrawalFormContext();
  const { paymentType, bankAccountId } = form.values;

  const { freePaymentTypes, paidPaymentTypes, isFetchingAvailablePaymentMethods } =
    useWithdrawalPaymentMethods({ paymentType });

  const { bankAccounts } = useAeropayLinkedBankAccounts({ enabled: isAeropayEnabled });

  const renderPaymentTypeList = (paymentTypes: typeof freePaymentTypes) =>
    paymentTypes.map(
      ([type, settings], index) =>
        type !== 'AEROPAY' && (
          <PaymentTypeRadio
            key={type}
            type={type}
            value={bankAccountId}
            settings={settings}
            isActive={type === paymentType && bankAccountId === undefined}
            superscriptIndex={index + 1}
            isNew={type === PaymentType.WHOP}
            onChange={() => {
              if (!settings.isDisabled) {
                form.reset();
                form.setFieldValue('paymentType', type);
                form.setFieldValue('bankAccountId', undefined);
                reportEvent('Transactions > Switch Withdrawal Method', { withdrawal_method: type });
                onWithdrawalMethodSelect?.();
              }
            }}
          />
        )
    );

  const isPaymentTypeAvailable = (
    paymentTypes: typeof freePaymentTypes,
    searchString: string
  ): boolean =>
    paymentTypes.some(([type, settings]) => type === searchString && !settings.isDisabled);

  // Show skeleton loading while data is being fetched
  if (isLoadingPaymentMethods) {
    return (
      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={24}>
        {/* Skeleton for saved bank accounts section */}
        {isAeropayEnabled && (
          <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16}>
            <Skeleton height={24} width="40%" />
            <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8}>
              <Skeleton height={56} />
              <Skeleton height={56} />
            </Stack>
          </Stack>
        )}

        {/* Skeleton for free payment types */}
        <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16}>
          <Skeleton height={24} width="30%" />
          <SimpleGrid cols={2} /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8}>
            <Skeleton height={96} />
            <Skeleton height={96} />
            <Skeleton height={96} />
            <Skeleton height={96} />
          </SimpleGrid>
        </Stack>

        {/* Skeleton for paid payment types */}
        <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16}>
          <Skeleton height={24} width="35%" />
          <SimpleGrid cols={2} /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8}>
            <Skeleton height={96} />
            <Skeleton height={96} />
          </SimpleGrid>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={24}>
      {/* Aeropay bank accounts */}
      {isAeropayEnabled &&
        bankAccounts?.length > 0 &&
        isPaymentTypeAvailable(freePaymentTypes, 'AEROPAY') && (
          <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16} align="center">
            <Title variant="subhead-medium" align="left" w="100%">
              {t('withdrawal.form.select.labelSaved')}
            </Title>
            <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8} w="100%">
              {bankAccounts?.map((bankAccount, index) => (
                <PaymentTypeRadio
                  key={bankAccount.bankAccountId}
                  type={PaymentType.AEROPAY}
                  value={bankAccount.bankAccountId}
                  settings={{ isFree: true, isDisabled: false }}
                  isActive={bankAccountId === bankAccount.bankAccountId}
                  superscriptIndex={index + 1}
                  isWithdrawal={false}
                  onChange={() => {
                    form.reset();
                    form.setFieldValue('paymentType', PaymentType.AEROPAY);
                    form.setFieldValue('bankAccountId', bankAccount.bankAccountId);
                    form.setFieldValue('bankName', bankAccount.bankName);
                    form.setFieldValue('bankAccountName', bankAccount.name);
                    onWithdrawalMethodSelect?.();
                  }}
                  bankAccount={bankAccount}
                  isDeleteBankAccountVisible
                />
              ))}
            </Stack>
          </Stack>
        )}

      {/* Free payment types */}
      {(isFetchingAvailablePaymentMethods || freePaymentTypes.length > 0) && (
        <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16}>
          <Title variant="subhead-medium" align="left" w="100%">
            {t('withdrawal.form.select.labelFree')}
          </Title>
          <SimpleGrid cols={2} /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8} w="100%">
            {renderPaymentTypeList(freePaymentTypes)}
          </SimpleGrid>
        </Stack>
      )}

      {/* Paid payment types */}
      {(isFetchingAvailablePaymentMethods || paidPaymentTypes.length > 0) && (
        <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16}>
          <Title variant="subhead-medium" align="left" w="100%">
            {t('withdrawal.form.select.labelPaid')}
          </Title>
          <SimpleGrid cols={2} /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8} w="100%">
            {renderPaymentTypeList(paidPaymentTypes)}
          </SimpleGrid>
        </Stack>
      )}
    </Stack>
  );
}
